import { ChevronLeft, ChevronRight, ShieldQuestion } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/use-current-user";

import { type ClaimWithRelations, useClaim } from "../../api/use-claim";
import { useDiscardCircumstance } from "../../api/use-claim-insights";
import { InsightFeedback } from "../ai-insights/insight-feedback";
import { CircumstanceCard } from "./circumstance-card";
import { type ExtraordinaryCircumstance, resolveCircumstance } from "./circumstance-config";

/**
 * Priority display of detected extraordinary circumstances (CX) on the claim overview.
 * Shown to Upclaim / LAWYER whenever the claim's `CX` insight carries at least one detected
 * event with medium-or-high confidence. A jurist can reject ("discard") a detection — it then
 * collapses into a discreet, restorable toggle. Port of ClaimCircumstances.vue.
 */

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function cxKey(cx: ExtraordinaryCircumstance): string {
  return `${cx.cxType}|${cx.iata ?? ""}|${cx.date ?? ""}`;
}

/**
 * Confidently-detected circumstances from the claim's CX insight (medium+
 * confidence, low-confidence noise hidden), split by discarded flag.
 */
export function getRelevantCx(claim: ClaimWithRelations | undefined): {
  active: ExtraordinaryCircumstance[];
  discarded: ExtraordinaryCircumstance[];
} {
  const insight = claim?.insights?.find((candidate) => candidate?.type?.type === "CX");
  const rawMetadata = insight?.metadata;
  const parsed = typeof rawMetadata === "string" ? safeParse(rawMetadata) : rawMetadata;
  const detected: ExtraordinaryCircumstance[] = Array.isArray(parsed)
    ? (parsed as ExtraordinaryCircumstance[])
    : [];

  const relevant = detected.filter(
    (cx) => cx.eventDetected && ["high", "medium"].includes((cx.confidence ?? "").toLowerCase()),
  );

  return {
    active: relevant.filter((cx) => !cx.discarded),
    discarded: relevant.filter((cx) => cx.discarded),
  };
}

interface ClaimCircumstancesProps {
  claimId: string;
  dense?: boolean;
  /** Force the active card fully open (no header-only preview) — e.g. the incident study's CX section. */
  forceFull?: boolean;
}

export function ClaimCircumstances({ claimId, dense = false, forceFull = false }: ClaimCircumstancesProps) {
  const { can } = useCurrentUser();
  const { data: claim } = useClaim(claimId);
  const discardCircumstance = useDiscardCircumstance(claimId);

  // Which rejected detections the user has expanded back open.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // When several active circumstances are detected, browse them one at a time instead of stacking them.
  const [activeIndex, setActiveIndex] = useState(0);

  const isLegalUser = can([...UPCLAIM_ROLES, "LAWYER"]);

  const { active, discarded } = getRelevantCx(claim);
  const cxInsight = claim?.insights?.find((insight) => insight?.type?.type === "CX") ?? null;

  if (!isLegalUser || active.length + discarded.length === 0) {
    return null;
  }

  const pendingKey =
    discardCircumstance.isPending && discardCircumstance.variables
      ? `${discardCircumstance.variables.cxType}|${discardCircumstance.variables.iata}|${discardCircumstance.variables.date}`
      : null;

  async function setDiscarded(cx: ExtraordinaryCircumstance, discardedValue: boolean) {
    try {
      await discardCircumstance.mutateAsync({
        cxType: cx.cxType,
        iata: cx.iata ?? "",
        date: cx.date ?? "",
        discarded: discardedValue,
      });
      if (discardedValue) {
        setExpanded((current) => ({ ...current, [cxKey(cx)]: false }));
      }
    } catch {
      // Error toast handled by notify.promise inside the mutation.
    }
  }

  function toggleExpanded(cx: ExtraordinaryCircumstance) {
    const key = cxKey(cx);
    setExpanded((current) => ({ ...current, [key]: !current[key] }));
  }

  const currentActiveIndex = Math.min(activeIndex, Math.max(0, active.length - 1));
  const currentActive = active[currentActiveIndex];

  // Header-only preview (click to expand) by default; shown in full only when a caller forces it
  // — e.g. the incident study's CX section.
  const showFull = forceFull;

  return (
    <section className="space-y-3 px-4 text-left">
      {/* Active (non-rejected) detections — one at a time, paged when there are several */}
      {currentActive ? (
        <div>
          {active.length > 1 ? (
            <div className="mb-2 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="icon-sm"
                disabled={currentActiveIndex === 0}
                onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                aria-label="Circonstance précédente"
              >
                <ChevronLeft />
              </Button>
              <span className="text-xs tabular-nums text-gray-500">
                {currentActiveIndex + 1}/{active.length}
              </span>
              <Button
                variant="outline"
                size="icon-sm"
                disabled={currentActiveIndex === active.length - 1}
                onClick={() => setActiveIndex((i) => Math.min(active.length - 1, i + 1))}
                aria-label="Circonstance suivante"
              >
                <ChevronRight />
              </Button>
            </div>
          ) : null}
          <CircumstanceCard
            key={cxKey(currentActive)}
            circumstance={currentActive}
            dense={dense}
            previewMode={!showFull}
            busy={pendingKey === cxKey(currentActive)}
            onDiscard={() => {
              setDiscarded(currentActive, true);
              setActiveIndex((i) => Math.min(i, Math.max(0, active.length - 2)));
            }}
            footer={
              // Jurist feedback asked in the card, where the detection is read — one per claim.
              <InsightFeedback
                claimId={claimId}
                insight={cxInsight}
                question="Cette détection est-elle correcte ?"
                className="border-t-0 pt-0"
              />
            }
          />
        </div>
      ) : null}

      {/* Rejected detections: discreet, restorable toggles */}
      {discarded.map((cx) => (
        <div key={cxKey(cx)}>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md border border-dashed border-gray-200 bg-gray-50/60 px-3 py-2 text-left text-xs text-gray-500 hover:bg-gray-100"
            onClick={() => toggleExpanded(cx)}
          >
            <ShieldQuestion size={14} strokeWidth={1.8} className="flex-none text-gray-400" />
            <span className="min-w-0 flex-1 truncate">
              Détection rejetée — {resolveCircumstance(cx.cxType).typeLabel}
              {cx.iata ? <span> · {cx.iata}</span> : null}
            </span>
            <span className="flex-none text-[11px] text-gray-400">
              {expanded[cxKey(cx)] ? "Masquer" : "Afficher"}
            </span>
          </button>

          {expanded[cxKey(cx)] ? (
            <div className="mt-2 opacity-80">
              <CircumstanceCard
                circumstance={cx}
                dense={dense}
                variant="compact"
                actionText="Restaurer la détection"
                busy={pendingKey === cxKey(cx)}
                onDiscard={() => setDiscarded(cx, false)}
              />
            </div>
          ) : null}
        </div>
      ))}

      {/* No active detection left (all rejected): the feedback stays reachable under the list. */}
      {!currentActive ? (
        <InsightFeedback
          claimId={claimId}
          insight={cxInsight}
          question="Cette détection est-elle correcte ?"
          className="border-gray-200"
        />
      ) : null}
    </section>
  );
}
